/**
 * Route model.
 *
 * A route is an ordered list of waypoints. Legs are rhumb lines: a leg is run at
 * a constant bearing, which is what the helm actually steers and what a
 * chartplotter draws. Over a coastal leg the difference from a great circle is
 * far below steering error; over an ocean passage it is not, and the model says
 * so explicitly rather than leaving it implicit.
 *
 * All distances metres, bearings degrees true, times seconds.
 */

import {
  type LatLon,
  greatCircleDistance,
  isValidLatLon,
  rhumbBearing,
  rhumbDistance,
  sampleLeg,
} from './geo';
import { estimatePassage, type BoatProfile, type PassageEstimate } from './boat';

export interface Waypoint extends LatLon {
  /** Stable identifier, so reordering does not confuse React keys or undo. */
  id: string;
  name?: string;
}

export interface Route {
  id: string;
  name: string;
  waypoints: Waypoint[];
  /** Epoch milliseconds. */
  createdAt: number;
  updatedAt: number;
  notes?: string;
}

export interface RouteLeg {
  index: number;
  from: Waypoint;
  to: Waypoint;
  /** Rhumb-line distance, metres. */
  distanceM: number;
  /** Constant bearing to steer, degrees true. */
  bearingDeg: number;
  /** Great-circle distance for the same leg, metres — for passage comparison. */
  greatCircleDistanceM: number;
}

export function createRoute(id: string, name = 'New route', now = Date.now()): Route {
  return { id, name, waypoints: [], createdAt: now, updatedAt: now };
}

/** Legs between consecutive waypoints. A route of n waypoints has n-1 legs. */
export function routeLegs(route: Pick<Route, 'waypoints'>): RouteLeg[] {
  const legs: RouteLeg[] = [];
  for (let i = 0; i < route.waypoints.length - 1; i++) {
    const from = route.waypoints[i];
    const to = route.waypoints[i + 1];
    legs.push({
      index: i,
      from,
      to,
      distanceM: rhumbDistance(from, to),
      bearingDeg: rhumbBearing(from, to),
      greatCircleDistanceM: greatCircleDistance(from, to),
    });
  }
  return legs;
}

/** Total route distance along rhumb-line legs, metres. */
export function routeDistance(route: Pick<Route, 'waypoints'>): number {
  return routeLegs(route).reduce((total, leg) => total + leg.distanceM, 0);
}

/** Cumulative distance from the start to each waypoint, metres. */
export function cumulativeDistances(route: Pick<Route, 'waypoints'>): number[] {
  const distances: number[] = [];
  let total = 0;
  route.waypoints.forEach((_, i) => {
    if (i > 0) total += rhumbDistance(route.waypoints[i - 1], route.waypoints[i]);
    distances.push(total);
  });
  return distances;
}

/** Time and fuel for the whole route at the boat's cruise speed. */
export function estimateRoute(route: Pick<Route, 'waypoints'>, boat: BoatProfile): PassageEstimate {
  return estimatePassage(routeDistance(route), boat);
}

// --- Editing (pure; every operation returns a new Route) --------------------

const touch = (route: Route, waypoints: Waypoint[], now: number): Route => ({
  ...route,
  waypoints,
  updatedAt: now,
});

export function addWaypoint(route: Route, waypoint: Waypoint, now = Date.now()): Route {
  if (!isValidLatLon(waypoint)) throw new RangeError('waypoint is not a valid position');
  return touch(route, [...route.waypoints, waypoint], now);
}

export function insertWaypoint(route: Route, index: number, waypoint: Waypoint, now = Date.now()): Route {
  if (!isValidLatLon(waypoint)) throw new RangeError('waypoint is not a valid position');
  const clamped = Math.max(0, Math.min(index, route.waypoints.length));
  const waypoints = [...route.waypoints];
  waypoints.splice(clamped, 0, waypoint);
  return touch(route, waypoints, now);
}

export function removeWaypoint(route: Route, id: string, now = Date.now()): Route {
  const waypoints = route.waypoints.filter((w) => w.id !== id);
  return waypoints.length === route.waypoints.length ? route : touch(route, waypoints, now);
}

export function moveWaypoint(route: Route, id: string, position: LatLon, now = Date.now()): Route {
  if (!isValidLatLon(position)) throw new RangeError('position is not a valid position');
  let changed = false;
  const waypoints = route.waypoints.map((w) => {
    if (w.id !== id) return w;
    changed = true;
    return { ...w, lat: position.lat, lon: position.lon };
  });
  return changed ? touch(route, waypoints, now) : route;
}

export function reorderWaypoint(route: Route, from: number, to: number, now = Date.now()): Route {
  const n = route.waypoints.length;
  if (from < 0 || from >= n || to < 0 || to >= n || from === to) return route;
  const waypoints = [...route.waypoints];
  const [moved] = waypoints.splice(from, 1);
  waypoints.splice(to, 0, moved);
  return touch(route, waypoints, now);
}

export function reverseRoute(route: Route, now = Date.now()): Route {
  return touch(route, [...route.waypoints].reverse(), now);
}

// --- Safety checking --------------------------------------------------------

/**
 * How finely a leg is sampled when checking it against charted depths, metres.
 *
 * A route is only as safe as the shallowest water beneath it, and hazards do not
 * politely place themselves at waypoints. 50 m is fine enough to catch an
 * isolated rock at typical harbour-chart scales without producing so many probe
 * points that the check stalls the UI on a long passage.
 */
export const DEPTH_CHECK_SPACING_M = 50;

export interface RouteProbe {
  legIndex: number;
  position: LatLon;
  /** Distance from the route start to this probe, metres. */
  distanceAlongRouteM: number;
}

/**
 * Positions at which a route should be tested against the chart.
 *
 * This produces the probe points only — it deliberately does not query the
 * chart. Depth lookup needs the rendered map (it reads back the tiles actually
 * loaded), so it lives in the chart layer; keeping the sampling here means the
 * geometry is unit-testable without a map instance.
 */
export function routeProbes(
  route: Pick<Route, 'waypoints'>,
  spacingM: number = DEPTH_CHECK_SPACING_M,
): RouteProbe[] {
  const probes: RouteProbe[] = [];
  let distanceSoFar = 0;

  for (const leg of routeLegs(route)) {
    const points = sampleLeg(leg.from, leg.to, spacingM);
    // Drop the duplicated join between consecutive legs, except on the first.
    const start = leg.index === 0 ? 0 : 1;
    const step = leg.distanceM / Math.max(1, points.length - 1);
    for (let i = start; i < points.length; i++) {
      probes.push({
        legIndex: leg.index,
        position: points[i],
        distanceAlongRouteM: distanceSoFar + step * i,
      });
    }
    distanceSoFar += leg.distanceM;
  }
  return probes;
}

export type RouteWarningKind =
  | 'shallow'
  | 'hazard'
  | 'low_clearance'
  | 'restricted'
  | 'no_chart_data';

export interface RouteWarning {
  kind: RouteWarningKind;
  legIndex: number;
  position: LatLon;
  distanceAlongRouteM: number;
  /** Human-readable, already unit-formatted by the caller. */
  message: string;
  /**
   * `danger` blocks safe passage for this boat; `caution` needs a decision from
   * the skipper. Nothing here is advisory-only — if it is not worth a decision
   * it should not be a warning at all.
   */
  severity: 'danger' | 'caution';
}

/** Order warnings the way a skipper reads them: worst first, then by position. */
export function sortWarnings(warnings: readonly RouteWarning[]): RouteWarning[] {
  const rank = { danger: 0, caution: 1 } as const;
  return [...warnings].sort(
    (a, b) => rank[a.severity] - rank[b.severity] || a.distanceAlongRouteM - b.distanceAlongRouteM,
  );
}

// --- Validation -------------------------------------------------------------

export function isNavigable(route: Pick<Route, 'waypoints'>): boolean {
  return route.waypoints.length >= 2;
}

/** Reject routes that cannot be stored or shared. */
export function validateRoute(route: Route): string[] {
  const problems: string[] = [];
  if (!route.name.trim()) problems.push('Route name is required');
  route.waypoints.forEach((w, i) => {
    if (!isValidLatLon(w)) problems.push(`Waypoint ${i + 1} has an invalid position`);
  });
  const ids = new Set(route.waypoints.map((w) => w.id));
  if (ids.size !== route.waypoints.length) problems.push('Waypoint identifiers must be unique');
  return problems;
}
